import { useCallback, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { committedActivitiesKey, scheduleKey } from '@constants/queryKeys';
import { useSchedulerActions } from '../../SchedulerPage/hooks/useSchedulerStore';
import { useRestoreServerState } from '../../SchedulerPage/hooks/useRestoreServerState';
import { immediateWriteKey, writeImmediate } from '../../SchedulerPage/lib/immediateWriteChannel';
import {
  AREA_REPORTS_CHANNEL,
  areaReport,
  mergeAreaReports,
  sendAreaReport,
  type IAreaReportWrite,
} from '../../SchedulerPage/lib/areaReportWrite';
import { applyAreaReport, openProgressSession } from '@d-orca/shared/client/activities';
import { useCurrentActor } from '@hooks/useCurrentActor';
import type { ICommittedActivity } from '@apis/activities';
import type { ISchedulerData, ISchedulerTask, WritableLocationStatus } from '@interfaces/IScheduler';

/** This schedule's row of the committed feed for `taskId` — the task as the
 *  tracker draws it, with Trusstor's live per-area status. A task id is unique
 *  only within one schedule (a copied programme keeps its ids). */
function feedRow(qc: QueryClient, siteId: string, scheduleNumber: string, taskId: string): ICommittedActivity | undefined {
  return qc
    .getQueryData<ICommittedActivity[]>(committedActivitiesKey(siteId))
    ?.find((row) => row.scheduleNumber === scheduleNumber && row.task.id === taskId);
}

/** Patch one task in both caches that hold it: the committed feed the tracker
 *  renders from, and the schedule doc the rest of the page reads (preserving
 *  its other fields — crucially the `dependencies` reference, which keeps the
 *  shared critical-path memo stable across a status-only edit). */
function patchTaskInCache(
  qc: QueryClient,
  siteId: string,
  scheduleNumber: string,
  taskId: string,
  patch: Partial<ISchedulerTask>,
): void {
  qc.setQueryData<ICommittedActivity[]>(committedActivitiesKey(siteId), (rows) =>
    rows?.map((row) =>
      row.scheduleNumber === scheduleNumber && row.task.id === taskId ? { ...row, task: { ...row.task, ...patch } } : row,
    ),
  );
  qc.setQueryData<ISchedulerData>(scheduleKey(siteId, scheduleNumber), (old) =>
    old ? { ...old, tasks: old.tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)) } : old,
  );
}

/**
 * Write a task's area status straight from a Progress Tracker cell. Mirrors what
 * the Scheduler's area board does for a single tap: sets the area status and,
 * when the tap moved the number of done areas, shows the re-blended task %
 * (a review submission, its undo and a reject move no % — area review stage
 * spec, ruling 8).
 *
 * The redux store (so an activity drawer reflects it) and the query caches (the
 * committed feed the tracker renders from, and the schedule doc) are patched
 * OPTIMISTICALLY, so the cell and column % move at once. The task is read from
 * the feed, never the schedule doc: only the feed carries Trusstor's live area
 * status (the doc's reads `planned`), so a take-back from review must be
 * compared against the feed. The returned callback is stable (it reads the
 * cache, not a subscribed store slice), so cells stay memoised.
 *
 * The write is the area report alone, on the task's shared area channel
 * (`areaReportWrite` — serialized and merged with the drawer's): the SERVER
 * writes Trusstor, then derives and stores the % (QA R-04). Its % replaces the
 * shown one unless a newer tap on the task is still to land. A report the
 * channel gives up is not rolled back from a snapshot — that could undo another
 * cell's tap that did land — but from a REFETCH: the feed and the schedule are
 * re-read, the store with them, and `onFailed` gets the error once, so the
 * page can say what happened.
 */
export function useTrackerAreaWrite(
  siteId: string,
  scheduleNumber: string | undefined,
  onFailed: (error: unknown) => void,
): (taskId: string, zoneId: string, status: WritableLocationStatus) => void {
  const qc = useQueryClient();
  const actor = useCurrentActor();
  const restore = useRestoreServerState(siteId);
  const { taskLocationStatusChanged, taskProgressSet } = useSchedulerActions();
  // Per-task tap counter: a response settles the % only when no tap on the task
  // came after the ones it carried.
  const tapsRef = useRef<Map<string, number>>(new Map());

  return useCallback(
    (taskId, zoneId, status) => {
      if (!scheduleNumber) return;
      // The feed, not the schedule doc: only the feed carries Trusstor's live
      // area status, which is what the cell showed and what `status` moves from.
      const task = feedRow(qc, siteId, scheduleNumber, taskId)?.task;
      if (!task) return;
      // Each tap is its own session: the stored % is the base. An explicit
      // report makes the new STATUS the truth for this area — the stale
      // imported/seeded per-location progressPercent is dropped, which would
      // otherwise override status in the tracker's cell percent
      // (locationPercent) and freeze the floor rollup at the old value.
      const { targets, nextLocations, pct } = applyAreaReport(openProgressSession(task), task.locations ?? [], [zoneId], status);
      if (targets.length === 0) return; // not this task's area, or already there

      // Optimistic store update — an activity drawer opened later reads the store.
      // Log meta mirrors the server's structured `location` entry optimistically.
      taskLocationStatusChanged({
        siteId, taskId, locationId: zoneId, status,
        activityId: `act-${crypto.randomUUID()}`, at: new Date().toISOString(), by: actor.id,
      });
      if (pct !== null) taskProgressSet({ siteId, taskId, progressPercent: pct });
      // Optimistic query patch — the tracker (and Daily List) render from the query.
      patchTaskInCache(qc, siteId, scheduleNumber, taskId, { locations: nextLocations, progressPercent: pct ?? task.progressPercent });

      const taps = (tapsRef.current.get(taskId) ?? 0) + 1;
      tapsRef.current.set(taskId, taps);
      const ctx = { siteId, scheduleNumber, taskId };
      writeImmediate<IAreaReportWrite>(
        immediateWriteKey(AREA_REPORTS_CHANNEL, siteId, scheduleNumber, taskId),
        areaReport([zoneId], status, false),
        async (report) => {
          const sentAfter = tapsRef.current.get(taskId);
          const { progressPercent } = await sendAreaReport(ctx, report);
          if (tapsRef.current.get(taskId) !== sentAfter) return;
          patchTaskInCache(qc, siteId, scheduleNumber, taskId, { progressPercent });
          taskProgressSet({ siteId, taskId, progressPercent });
        },
        (error) => {
          void qc.invalidateQueries({ queryKey: committedActivitiesKey(siteId) });
          void restore(scheduleNumber);
          onFailed(error);
        },
        { coalesce: mergeAreaReports, debounceMs: 0 },
      );
    },
    [qc, siteId, scheduleNumber, taskLocationStatusChanged, taskProgressSet, onFailed, actor.id, restore],
  );
}
